import React, { useState, useEffect, useRef, useMemo } from "react";
import { Col, Row } from "react-bootstrap";
import { ThreeCircles } from "react-loader-spinner";
import CygnusGrid from "../../../../reusable_components/CygnusGrid/CygnusGrid";
import {
  ClientPorterOption,
  ClientSelectorProps,
  ClientTypeOption,
  columnDefinitionForEngagementGrid,
  getPreSelectedRows,
  enforceSingleSelection
} from "./ClientSelector.utils";
import { fetchFirmClients } from "../../../../client_onboarding/ClientsGrid/ClientsGrid.service";
import { CygnusGridRef } from "../../../../reusable_components/CygnusGrid/CygnusGrid.utils";
import { useDebounce } from "../../../../client_onboarding/ClientsGrid/ClientsGrid.utils";
import Select from "react-select";
import { fetchClientGroups } from "../../../../client_onboarding/ClientsGrid/ClientsGrid.service";
import { fetchAllClients } from "./ClientSelector.service";
import styles from "./ClientSelector.module.css";

/**
 * ExistingClientSelector Component
 *
 * A wizard page component for selecting a client (existing or new) and choosing the client type.
 * - Integrates with CygnusGrid to list firm clients for selection
 * - Caches grid data and maintains selected row in sharedData
 * - Provides validation status back to the parent wizard controller
 */
const ExistingClientSelector: React.FC<ClientSelectorProps> = ({
  sharedData,
  setSharedData,
  onValidationChange,
}) => {
  const [clientTypes, setClientTypes] = useState<ClientTypeOption[]>([
    { id: "INDIVIDUAL", label: "Individual Client", checked: true },
    { id: "ENTITY", label: "Entity Client", checked: false },
  ]);

  const [clientPorterTypes, setClientPorterTypes] = useState<
    ClientPorterOption[]
  >([
    { id: 1, label: "An Existing Client", checked: true },
    { id: 2, label: "A New Client", checked: false },
  ]);

  const [columnDefs, setColumnDefs] = useState<any[]>([]);

  const [clientFetchAPIProgress, setClientFetchAPIProgress] =
    useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>();
  const gridRef = useRef<CygnusGridRef>(null);
  
  const debouncedSearchText = useDebounce(searchText, 3000);
  const [clientGroupsOptions, setClientGroupsOptions] = useState<any[]>([]);
  type OptionType = { label: string; value: number; id: number };
  const [selectedClientGroup, setSelectedClientGroup] =
    useState<OptionType | null>(null);

  useEffect(() => {
    if (debouncedSearchText !== undefined) {
      fetchAllClients(setSharedData, debouncedSearchText, setClientFetchAPIProgress);
    }
  }, [debouncedSearchText]);
  
  /**
   * Handles changing the selected client type (Individual vs Entity)
   */
  const onClientTypeChange = (type: ClientTypeOption) => {
    const updatedTypes = clientTypes.map((item) => ({
      ...item,
      checked: item.id === type.id,
    }));
    setClientTypes(updatedTypes);
    setSharedData((prev) => ({
      ...prev,
      selectedClientTypeCode: type.id,
    }));
  };

  /**
   * Handles changing the client source (Existing vs New)
   */
  const onClientPorterChange = (type: ClientPorterOption) => {
    const updatedPorters = clientPorterTypes.map((item) => ({
      ...item,
      checked: item.id === type.id,
    }));
    setClientPorterTypes(updatedPorters);
    setSharedData((prev) => ({
      ...prev,
      selectedClientPorterId: type.id,
    }));
  };

  /**
   * This is a validation checker function for client selection page
   */
  useEffect(() => {
    const validateClientStep = () => {
      const isExistingClient = sharedData.selectedClientPorterId === 1;
      const hasSelectedClients =
        Array.isArray(sharedData.clientMasterIds) &&
        sharedData.clientMasterIds.length > 0;
      return isExistingClient ? hasSelectedClients : true;
    };

    const isValid = validateClientStep();
    onValidationChange(isValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedData.clientMasterIds, sharedData.selectedClientPorterId]);

  useEffect(() => {
    gridRef.current?.refreshGrid?.();
  }, [sharedData.groupClientsData]);

  useEffect(() => {
    setColumnDefs(columnDefinitionForEngagementGrid);
    // getClientGroups();
  }, []);

  const preSelectedRows = useMemo(() => {
    if (!sharedData.allClientsData || !sharedData.clientMasterIds) return [];
    return getPreSelectedRows(
      sharedData.allClientsData,
      "ClientMasterId",
      sharedData.clientMasterIds[0]
    );
  }, [sharedData.allClientsData, sharedData.clientMasterIds]);

  const getClientGroups = async () => {
    const data: any[] = await fetchClientGroups();
    const optionsData = data.map((group) => ({
      label: group.label,
      value: group.value,
    }));
    setSharedData((prev) => ({
      ...prev,
      clientGroupData: data,
    }));
    setClientGroupsOptions(optionsData);
  };

  const handleClientGroupChange = async (selectedOption: any) => {
    setSelectedClientGroup(selectedOption);

    if (!selectedOption) {
      setSharedData((prev) => ({
        ...prev,
        clientGroupCode: undefined,
        groupClientsData: undefined,
        clientMasterIds: [], // Optional: Reset selection
      }));
      return;
    }

    const response = await fetchFirmClients({
      offset: 0,
      limit: 10,
      searchText: "",
      sourceCode: "CCH_AXCESS",
      clientGroupCode: selectedOption.value,
    });

    const data = response.records;

    setSharedData((prev) => ({
      ...prev,
      clientGroupCode: selectedOption.value,
      groupClientsData: data,
      clientMasterIds: [], // Optional: Reset selection
    }));
  };


  return (
    <Row>
      <div className="flex-column-center-center">
        <h6>Client Select</h6>
        <p>Select an existing or a new client for your engagement</p>
      </div>

      <div style={{ marginBottom: 20 }}>
        <p style={{ marginBottom: 10, marginLeft: 10, fontWeight: 600 }}>
          Select Client Type
        </p>
        <div className="flex-row-center-start">
          {clientTypes.map((type) => (
            <div
              key={type.id}
              style={{ width: "fit-content" }}
              onClick={() => onClientTypeChange(type)}
            >
              <input
                type="radio"
                className="form-check-input move-left custom-check-input"
                checked={type.checked}
                readOnly
              />
              {type.label}
            </div>
          ))}
        </div>
      </div>

      <Col md={5} style={{ marginBottom: 20 }}>
        <p style={{ marginBottom: 10, marginLeft: 10, fontWeight: 600 }}>
          Who is this engagement letter for?
        </p>
        <div className="flex-row-center-start">
          {clientPorterTypes.map((type) => (
            <div
              key={type.id}
              style={{ width: "fit-content" }}
              onClick={() => onClientPorterChange(type)}
            >
              <input
                type="radio"
                className="form-check-input move-left custom-check-input"
                checked={type.checked}
                readOnly
              />
              {type.label}
            </div>
          ))}
        </div>
      </Col>
      <Col
        md={2}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <p style={{ margin: 0, fontWeight: 600 }}>OR</p>
      </Col>
      <Col md={5}>
        <p style={{ marginBottom: 2, fontWeight: 600 }}>Client Group</p>
        <Select
          placeholder="Select"
          value={selectedClientGroup}
          onChange={handleClientGroupChange}
          options={clientGroupsOptions}
          classNamePrefix="react-select"
        />
      </Col>

      {/* Check if the the selected client type is for an existing client */}
      {sharedData.selectedClientPorterId === 1 && (
        <>
          <label className={`form-label move-left ${styles.label}`}>
            {clientFetchAPIProgress ? (
              <Row className="flex-row-center-start" style={{ marginLeft: 5 }}>
                <div style={{ width: "fit-content", padding: 0 }}>
                  <ThreeCircles
                    height="15"
                    width="15"
                    color="#4fa94d"
                    visible={true}
                    ariaLabel="three-circles-rotating"
                    outerCircleColor="var(--theme1_tertiary)"
                    innerCircleColor="var(--theme1_secondary)"
                    middleCircleColor="var(--theme1_primary)"
                  />
                </div>
                <div style={{ width: "fit-content", padding: 0, marginLeft: 5 }}>Loading your clients ...</div>
              </Row>
            ) : (
              "Search client by Client ID"
            )}
          </label>

          <div style={{ padding: "0px 12px" }}>
            <input
              type="text"
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search..."
              className="form-control move-left custom-input"
            />
          </div>

          <div style={{ height: "300px" }}>
            <CygnusGrid
              ref={gridRef}
              columnDefs={columnDefs}
              fetchData={async () => {
                // Since this is not paginated, just return everything
                return { records: sharedData.allClientsData, totalCount: sharedData.allClientsData.length };
              }}
              dataIdentifier={"ClientMasterId"}
              rowSelection={"single"}
              rowModelType="clientSide"
              preSelectedRows={preSelectedRows}
              cachedIsLoading={clientFetchAPIProgress}
              cachedData={sharedData.allClientsData}
              onFirstDataRendered={(params) => {
                params.api.forEachNode((node: any) => {
                  enforceSingleSelection(
                    { node, data: node.data },
                    sharedData.clientMasterIds ?? [],
                    setSharedData
                  );
                });
              }}
              onRowSelected={(event: any) => {
                enforceSingleSelection(event, sharedData.clientMasterIds ?? [], setSharedData);
              }}
              onSelectionChange={(selectedRows: any[]) => {
                const selectedIds = selectedRows
                  .map((row) => row?.ClientMasterId)
                  .filter(Boolean);

                const prevId = sharedData.clientMasterIds?.[0];

                if (selectedIds.length === 0 && prevId) {
                  gridRef.current?.api?.forEachNode((node: any) => {
                    if (node.data?.ClientMasterId === prevId) {
                      node.setSelected(true);
                    }
                  });
                  return;
                }

                if (selectedIds.length > 0 && selectedIds[0] !== prevId) {
                  setSharedData((prev) => ({
                    ...prev,
                    clientMasterIds: [selectedIds[0]],
                    selectedClientMasterId: selectedIds[0],
                  }));
                }
              }}
            />
          </div>
        </>
      )}
    </Row>
  );
};

export default ExistingClientSelector;
